"use client";
import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from "react";

import LensColorCanvas from "@/src/utils/lens/lensColorCanvas";
import {
    tryonColorList,
    tryonRidingList,
    tryonSunColorList,
} from "@/config/lens/tryon_lens_color_list";
import { gaCommonEvent } from "@/src/utils/gtagEvents";
import { USER_PHOTO } from "@/config/interface"; // Assuming you have this interface defined
import { useTryonStore } from "@/store/modules/tryon";
import { colorSelectInit } from "@/config/filterConfig";
import { FmDrawer, FmIcon, FmTabs } from "@/ui-component";
import { tranColorByName } from "@/lib/commonService";
import ImageModal from "./ImageModal";
import classnames from "classnames";

import stl from "./tryon.module.scss";
import { useTranslation } from "@/src/i18n/client";
import acquisition from "../LoadAnalysis/analysis/DataAcquisition";

// TODO：这个文件的颜色选择模块应该更替为colorSelect模块
// Assuming other imports are here

enum TABTITLE {
    SELECTIMAGE = 0,
    FRANMECOLOR = 1,
    LENSTINT = 2,
}
interface SelectLineProps {
    imgList: USER_PHOTO[];
    onSelectVisibleImg: (item: USER_PHOTO) => void;
    currImg: USER_PHOTO | null;
    deleteImg: (item: USER_PHOTO, idx: number) => void;
    loading: boolean;
    onClickUpload: () => void;
    changeImgLoading: (loading: any) => void;
    setColorAni: (isShowColorAni: any) => void;
}

const SelectLine: React.FC<SelectLineProps> = (props) => {
    const [
        colorList,
        colorId,
        tryOnProduct,
        setTryOnActiveColor,
        changeTryOnColorPicture,
        changeTryOnPicture,
        changeTryOnLensColor,
    ] = useTryonStore((state) => [
        state.colorList,
        state.colorId,
        state.tryOnProduct,
        state.setTryOnActiveColor,
        state.changeTryOnColorPicture,
        state.changeTryOnPicture,
        state.changeTryOnLensColor,
    ]);

    const { t } = useTranslation("lens");
    const { changeImgLoading, setColorAni } = useMemo(() => props, [props]);

    const [initialPage, setInitialPage] = useState(0);
    const colorLensItemMemo = useMemo(() => {
        if (tryOnProduct.master_categories_id === 6) {
            return tryonSunColorList[0];
        } else if (tryOnProduct.master_categories_id === 14) {
            return tryonRidingList[0];
        } else {
            return tryonColorList[0];
        }
    }, [tryOnProduct.master_categories_id]);
    const [colorlensItem, setColorlensItem] = useState(colorLensItemMemo);
    const [showHelpContent, setShowHelpContent] = useState(false);
    const [canvasInit, setCanvasInit] = useState(false);
    let canvas = useRef<LensColorCanvas | null>(null);

    const onLensColorChange = useCallback(
        async (item: any) => {
            if (!canvasInit) {
                console.log("not canvasInit");
                return;
            }

            setColorlensItem(item);
            changeTryOnLensColor(item);

            if (item.title === "Clear") {
                changeTryOnColorPicture("");
                return;
            }

            gaCommonEvent("lens", {
                event_category: "try on",
                event_label: t(`lens:${item.title}`),
            });

            if (item.isColorAni && canvas.current) {
                changeTryOnPicture(canvas.current?.getImageTryOnImgAniMaster());
            }

            changeImgLoading(true);
            // 通过loading等事件循环下一帧更新，不影响颜色切换
            setTimeout(() => {
                if (canvas.current) {
                    canvas.current
                        .getImageTryOnImg({
                            color: item.color_rgb,
                            color_img: item.color_img,
                        })
                        .then((img: any) => {
                            changeTryOnColorPicture(img);
                            changeImgLoading(false);
                            setColorAni(!!item.isColorAni);
                        });
                }
            }, 0);
        },
        [
            canvasInit,
            changeImgLoading,
            changeTryOnColorPicture,
            changeTryOnLensColor,
            changeTryOnPicture,
            setColorAni,
            t,
        ],
    );

    const init = useCallback(
        async (activeColorItem: any) => {
            try {
                if (activeColorItem?.frame_image_total !== 3) {
                    return;
                }

                canvas.current = new LensColorCanvas();
                await canvas.current.init({
                    productId: tryOnProduct.products_id,
                    frame_image: activeColorItem.frame_image,
                });
                setCanvasInit(true);
                onLensColorChange(colorlensItem);
            } catch (e) {
                console.error(e);
            }
        },
        [colorlensItem, onLensColorChange, tryOnProduct.products_id],
    );

    useEffect(() => {
        (async () => {
            const activeColorItem = colorList.find(
                (item) => item.color_id === colorId,
            );

            await init(activeColorItem);
            setColorlensItem(colorLensItemMemo);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onTabClick = (val: any) => {
        setInitialPage(val);
    };

    const handleColorChange = useCallback(
        async (val: any) => {
            if (val.color_id === colorId) {
                return;
            }
            if (val.products_id && val.color_id) {
                acquisition.clickProduct(
                    "clickProduct",
                    `${val.products_id}-${val.color_id}`,
                );
            }

            if (val.frame_image_total === 3 && !canvas.current) {
                await init(val);
            }
            gaCommonEvent("frame", {
                event_category: "try on",
                event_label: val.color_name,
            });

            setTryOnActiveColor(val.color_id);

            if (val.frame_image_total === 3 && canvas.current) {
                changeImgLoading(true);
                canvas.current
                    ?.changeImg({
                        productId: val.products_id,
                        frame_image: val.frame_image,
                    })
                    .then(() => {
                        onLensColorChange(colorlensItem);
                        changeTryOnPicture(
                            canvas.current?.getImageTryOnImgAniMaster(),
                        );
                        changeImgLoading(false);
                    });
            }
        },
        [
            changeImgLoading,
            changeTryOnPicture,
            colorId,
            colorlensItem,
            init,
            onLensColorChange,
            setTryOnActiveColor,
        ],
    );

    const list = useMemo(() => {
        let res = [];
        const tempList =
            tryOnProduct.master_categories_id == 6
                ? tryonSunColorList
                : tryonColorList;
        if (tryOnProduct.dimensions_height < 40) {
            res = tempList.filter(
                (item: any) => !item.color_rgb || item.color_rgb.length < 2,
            );
            return res;
        }
        return tempList;
    }, [tryOnProduct.dimensions_height, tryOnProduct.master_categories_id]);

    const colorListDom = useMemo(() => {
        return colorList.map((val: any, key: number) => {
            let { colorClass, colorStyle } = colorSelectInit(val.color_name);

            return (
                <div
                    key={val.color_id}
                    onClick={() => handleColorChange(val)}
                    className={classnames(
                        stl["tryon-color-select-item"],
                        "tryon-color-select-item",
                        {
                            [stl["tryon-color-select-item-active"]]:
                                val.color_id === colorId,
                        },
                    )}
                >
                    <div
                        className={classnames(stl["inner"], `${colorClass}`)}
                        style={{ ...colorStyle, backgroundSize: "100% 100%" }}
                    />
                </div>
            );
        });
    }, [colorId, colorList, handleColorChange]);

    const colorListLensDom = useMemo(() => {
        return list.map((val, key: any) => {
            let { colorBg, title } = val;
            return (
                <div
                    key={title}
                    onClick={() => {
                        if (colorlensItem.title === title) return;
                        onLensColorChange(val);
                    }}
                    className={classnames(stl["tryon-color-select-item"], {
                        [stl["tryon-color-select-item-active"]]:
                            colorlensItem.title === title,
                    })}
                >
                    <div
                        className={classnames(stl["inner"], {
                            [stl["tryon-color-select-item-boder"]]:
                                title === "Clear" &&
                                colorlensItem.title !== title,
                        })}
                        style={{
                            background: colorBg,
                            backgroundSize: "100% 100%",
                        }}
                    />
                </div>
            );
        });
    }, [colorlensItem.title, list, onLensColorChange]);

    const activeColorItem = useMemo(
        () => colorList.find((item: any) => item.color_id == colorId),
        [colorId, colorList],
    );

    const handleOpenHelp = useCallback(() => {
        gaCommonEvent("lens help", {
            event_category: "try on",
        });
        setShowHelpContent(true);
    }, []);
    const initTabData = useMemo(
        () => [
            {
                label: t("lens:SelectImage"),
                value: TABTITLE.SELECTIMAGE,
                children: <ImageModal {...props} />,
            },
            {
                label: t("lens:FrameColor"),
                value: TABTITLE.FRANMECOLOR,
                children: (
                    <div>
                        <div
                            className={classnames(
                                stl["try-on-color-list"],
                                "try-on-color-list",
                            )}
                            style={{
                                justifyContent:
                                    colorList.length >= 7
                                        ? "flex-start"
                                        : "center",
                            }}
                        >
                            {colorListDom}
                        </div>
                        <div
                            className={stl["try-on-color-name"]}
                        >{`${tranColorByName({
                            t,
                            color_name: activeColorItem.color_name || "",
                        })} ${activeColorItem.color_number ? "(" + activeColorItem.color_number + ")" : ""}`}</div>
                    </div>
                ),
            },
            {
                label: t("lens:LensTint"),
                value: TABTITLE.LENSTINT,
                children: (
                    <>
                        <div
                            className={classnames(
                                stl["try-on-color-lens-list"],
                                "try-on-color-lens-list",
                            )}
                            style={{ justifyContent: "flex-start" }}
                        >
                            {colorListLensDom}
                        </div>
                        <div className={stl["try-on-color-name"]}>
                            <div style={{ marginRight: "0.1rem" }}>
                                {t(`lens:${colorlensItem.title}`)}
                            </div>
                            <FmIcon
                                onClick={handleOpenHelp}
                                icon="icon--FAQ"
                                style={{
                                    fontFamily: "var(--font-montserrat-bold)",
                                    fontSize: "0.44rem",
                                }}
                            />
                        </div>
                    </>
                ),
            },
        ],
        [
            activeColorItem?.color_name,
            activeColorItem?.color_number,
            colorList.length,
            colorListDom,
            colorListLensDom,
            colorlensItem?.title,
            handleOpenHelp,
            props,
            t,
        ],
    );
    const tabData = useMemo(() => {
        let tabTempData = initTabData;

        if (activeColorItem.frame_image_total !== 3) {
            tabTempData = tabTempData.filter(
                (item) => item.value !== TABTITLE.LENSTINT,
            ); // 不显示LensTint
        }
        if (!colorList || colorList.length < 1) {
            tabTempData = tabTempData.filter(
                (item: any) => item.value !== TABTITLE.FRANMECOLOR,
            );
        }
        return tabTempData;
    }, [activeColorItem.frame_image_total, colorList, initTabData]);

    return (
        <>
            <FmTabs
                tabs={tabData}
                value={initialPage}
                onChange={(e, value) => onTabClick(value)}
            ></FmTabs>
            <FmDrawer
                open={showHelpContent}
                onClose={() => setShowHelpContent(false)}
                closeable
                headerChildren
                closeablePosition="right"
                anchor={"bottom"}
            >
                {t(`lens:${colorlensItem.text}`)}
            </FmDrawer>
        </>
    );
};

export default SelectLine;
